import React, { useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  Edit2,
  Save,
  X,
  Upload,
  Download,
  Lock,
} from 'lucide-react';
import {
  getNcrById,
  createNcrAction,
  updateNcrAction,
  deleteNcrAction,
  createStageDependency,
  deleteStageDependency,
  uploadFileToAction,
  deleteAttachment,
  type Ncr,
  type NcrAction,
} from '../../services/ncrService.ts';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import './NcrDetail.css';

interface ActionFormState {
  stageIndex: number;
  actionIndex?: number;
  actionNumber?: number;
  targetDate?: string;
  closedDate?: string;
  completeDate?: string;
  actionDetails: string;
  actionResponse: string;
  ownerId?: number;
  isEditing: boolean;
}

const NcrDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const notify = useNotify();

  const [ncr, setNcr] = useState<Ncr | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedStages, setExpandedStages] = useState<Set<number>>(new Set());
  const [actionForm, setActionForm] = useState<ActionFormState | null>(null);
  const [uploadingFile, setUploadingFile] = useState<{
    stageIndex: number;
    actionIndex: number;
  } | null>(null);
  const [dependencyMode, setDependencyMode] = useState(false);
  const [selectedFromStage, setSelectedFromStage] = useState<number | null>(null);

  const formatStatusLabel = (value?: string) => {
    const normalized = (value || '').toLowerCase();
    if (normalized === 'in-progress') return 'In Progress';
    if (normalized === 'close') return 'Close';
    return 'Open';
  };

  const normalizeStatus = (value?: string) => {
    const normalized = (value || '').toLowerCase();
    if (normalized === 'draft' || normalized === 'open' || normalized === 'pending') return 'open';
    if (normalized === 'completed' || normalized === 'closed' || normalized === 'close') return 'close';
    if (normalized === 'in progress') return 'in-progress';
    return normalized === 'in-progress' ? 'in-progress' : 'open';
  };

  const formatDateOnly = (value?: string) => (value ? value.slice(0, 10) : '-');
  const toDatePickerValue = (value?: string | null) => {
    if (!value) return null;
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  };
  const toDateOnlyString = (value: Date | null) => {
    if (!value) return undefined;
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Load NCR data
  useEffect(() => {
    const fetchNcr = async () => {
      if (!id) return;
      try {
        const data = await getNcrById(Number(id));
        setNcr(data);
        // Auto-expand first stage
        if (data.stages && data.stages.length > 0) {
          setExpandedStages(new Set([0]));
        }
      } catch (error: any) {
        notify(error.message || 'Failed to load NCR', { type: 'warning' });
        navigate('/Ncr');
      } finally {
        setLoading(false);
      }
    };

    fetchNcr();
  }, [id, navigate, notify]);

  // Check if stage is locked based on dependencies
  const isStageLocked = (stageIndex: number): boolean => {
    if (!ncr || !ncr.stageDependencies) return false;

    // Check if this stage depends on any incomplete stages
    const dependencies = ncr.stageDependencies.filter(
      (dep) => dep.toStageId === ncr.stages?.[stageIndex]?.id && dep.dependencyType === 'sequential'
    );

    return dependencies.some((dep) => {
      const fromStage = ncr.stages?.find((s) => s.id === dep.fromStageId);
      return fromStage && normalizeStatus(fromStage.status) !== 'close';
    });
  };

  // Toggle stage expansion
  const toggleStageExpanded = (index: number) => {
    const newExpanded = new Set(expandedStages);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedStages(newExpanded);
  };

  // Open action form for creating/editing
  const openActionForm = (stageIndex: number, actionIndex?: number) => {
    if (!ncr) return;

    const stage = ncr.stages[stageIndex];
    let action: Partial<NcrAction> | undefined;

    if (actionIndex !== undefined) {
      action = stage?.actions?.[actionIndex];
    }

    setActionForm({
      stageIndex,
      actionIndex,
      actionNumber: action?.actionNumber ?? (actionIndex !== undefined ? actionIndex + 1 : stage.actions.length + 1),
      targetDate: action?.targetDate || '',
      closedDate: action?.closedDate || '',
      completeDate: action?.completeDate || '',
      actionDetails: action?.actionDetails || '',
      actionResponse: action?.actionResponse || '',
      ownerId: action?.ownerId,
      isEditing: actionIndex !== undefined,
    });
  };

  // Close action form
  const closeActionForm = () => {
    setActionForm(null);
  };

  // Save action (create or update)
  const saveAction = async () => {
    if (!actionForm || !ncr) return;

    const stage = ncr.stages[actionForm.stageIndex];
    if (!stage.id) {
      notify('Stage ID is required', { type: 'warning' });
      return;
    }

    try {
      const existingAction = actionForm.isEditing && actionForm.actionIndex !== undefined
        ? stage.actions[actionForm.actionIndex]
        : undefined;

      const actionData = {
        actionNumber: actionForm.actionNumber,
        targetDate: actionForm.targetDate,
        closedDate: actionForm.closedDate,
        completeDate: actionForm.completeDate,
        actionDetails: actionForm.actionDetails,
        actionResponse: actionForm.actionResponse,
        ownerId: actionForm.ownerId,
        status: existingAction?.status || 'open',
      };

      if (actionForm.isEditing && actionForm.actionIndex !== undefined) {
        // Update existing action
        const action = existingAction;
        if (!action) return;
        if (action.id) {
          await updateNcrAction(
            ncr.id!,
            stage.id,
            action.id,
            actionData
          );
          notify('Action updated successfully', { type: 'info' });
        }
      } else {
        // Create new action
        await createNcrAction(
          ncr.id!,
          stage.id,
          actionData
        );
        notify('Action created successfully', { type: 'info' });
      }

      // Refresh NCR data
      const updatedNcr = await getNcrById(ncr.id!);
      setNcr(updatedNcr);
      closeActionForm();
    } catch (error: any) {
      notify(error.message || 'Failed to save action', { type: 'warning' });
    }
  };

  // Delete action
  const handleDeleteAction = async (stageIndex: number, actionIndex: number) => {
    if (!ncr) return;

    const stage = ncr.stages[stageIndex];
    const action = stage.actions[actionIndex];

    if (!stage.id || !action.id) return;
    if (!window.confirm('Are you sure you want to delete this action?')) return;

    try {
      await deleteNcrAction(ncr.id!, stage.id, action.id);
      notify('Action deleted successfully', { type: 'info' });

      const updatedNcr = await getNcrById(ncr.id!);
      setNcr(updatedNcr);
    } catch (error: any) {
      notify(error.message || 'Failed to delete action', { type: 'warning' });
    }
  };

  // Handle file upload
  const handleFileUpload = async (stageIndex: number, actionIndex: number, file: File) => {
    if (!ncr) return;

    const stage = ncr.stages[stageIndex];
    const action = stage.actions[actionIndex];

    if (!stage.id || !action.id) return;

    setUploadingFile({ stageIndex, actionIndex });
    try {
      await uploadFileToAction(ncr.id!, stage.id, action.id, file);
      notify('File uploaded successfully', { type: 'info' });

      const updatedNcr = await getNcrById(ncr.id!);
      setNcr(updatedNcr);
    } catch (error: any) {
      notify(error.message || 'Failed to upload file', { type: 'warning' });
    } finally {
      setUploadingFile(null);
    }
  };

  // Handle file deletion
  const handleDeleteFile = async (attachmentId: number | undefined) => {
    if (!attachmentId) return;
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      await deleteAttachment(attachmentId);
      notify('File deleted successfully', { type: 'info' });

      if (ncr) {
        const updatedNcr = await getNcrById(ncr.id!);
        setNcr(updatedNcr);
      }
    } catch (error: any) {
      notify(error.message || 'Failed to delete file', { type: 'warning' });
    }
  };

  // Create stage dependency
  const handleCreateDependency = async (toStageId: number) => {
    if (!ncr || !selectedFromStage) return;

    try {
      await createStageDependency(ncr.id!, {
        fromStageId: selectedFromStage,
        toStageId,
        dependencyType: 'sequential',
      });

      notify('Dependency created successfully', { type: 'info' });
      const updatedNcr = await getNcrById(ncr.id!);
      setNcr(updatedNcr);
      setDependencyMode(false);
      setSelectedFromStage(null);
    } catch (error: any) {
      notify(error.message || 'Failed to create dependency', { type: 'warning' });
    }
  };

  // Delete stage dependency
  const handleDeleteDependency = async (dependencyId: number | undefined) => {
    if (!ncr || !dependencyId) return;
    if (!window.confirm('Are you sure you want to remove this dependency?')) return;

    try {
      await deleteStageDependency(ncr.id!, dependencyId);
      notify('Dependency removed successfully', { type: 'info' });

      const updatedNcr = await getNcrById(ncr.id!);
      setNcr(updatedNcr);
    } catch (error: any) {
      notify(error.message || 'Failed to delete dependency', { type: 'warning' });
    }
  };

  if (loading) {
    return <div className="ncr-detail-loading">Loading NCR details...</div>;
  }

  if (!ncr) {
    return <div className="ncr-detail-error">NCR not found</div>;
  }

  const normalizedNcrStatus = normalizeStatus(ncr.status);
  const ncrStatusLabel = formatStatusLabel(normalizedNcrStatus);

  return (
    <div className="ncr-detail-container">
      <div className="ncr-detail-header">
        <div className="header-info">
          <h1>{ncr.ncrNumber}</h1>
          <p className="header-subtitle">
            Template: {ncr.templateName} | Status: <span className={`status-${normalizedNcrStatus || 'open'}`}>{ncrStatusLabel}</span>
          </p>
        </div>
        <div className="header-actions">
          <button
            className="btn-secondary"
            onClick={() => navigate(`/Ncr/${ncr.id}/edit`)}
          >
            <Edit2 size={18} /> Edit
          </button>
          <button
            className="btn-secondary"
            onClick={() => navigate('/Ncr')}
          >
            Back to List
          </button>
        </div>
      </div>

      <div className="ncr-detail-content">
        {/* NCR Overview */}
        <div className="info-section">
          <h2>Overview</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Description</label>
              <p>{ncr.description || '-'}</p>
            </div>
            <div className="info-item">
              <label>Raised Date</label>
              <p>{formatDateOnly(ncr.raisedDate)}</p>
            </div>
            <div className="info-item">
              <label>Target Date</label>
              <p>{formatDateOnly(ncr.targetDate)}</p>
            </div>
            <div className="info-item">
              <label>Severity</label>
              <p>{ncr.severity || '-'}</p>
            </div>
            <div className="info-item">
              <label>Owner</label>
              <p>{ncr.ownerName || '-'}</p>
            </div>
            <div className="info-item">
              <label>Created</label>
              <p>{ncr.createdAt ? new Date(ncr.createdAt).toLocaleDateString() : '-'}</p>
            </div>
            <div className="info-item">
              <label>Last Updated</label>
              <p>{ncr.updatedAt ? new Date(ncr.updatedAt).toLocaleDateString() : '-'}</p>
            </div>
          </div>
        </div>

        {/* Stages and Actions */}
        <div className="stages-section">
          <div className="stages-header">
            <h2>Workflow Stages</h2>
            <button
              className="btn-sm btn-secondary"
              onClick={() => setDependencyMode(!dependencyMode)}
            >
              {dependencyMode ? 'Cancel' : 'Configure Dependencies'}
            </button>
          </div>

          <div className="stages-container">
            {ncr.stages.map((stage, stageIndex) => {
              const isLocked = isStageLocked(stageIndex);
              return (
                <div key={stageIndex} className={`stage-panel ${isLocked ? 'locked' : ''}`}>
                  <div
                    className="stage-header-bar"
                    onClick={() => !dependencyMode && toggleStageExpanded(stageIndex)}
                  >
                    <div className="stage-title">
                      {expandedStages.has(stageIndex) ? (
                        <ChevronUp size={20} />
                      ) : (
                        <ChevronDown size={20} />
                      )}
                      <span className="stage-number">Stage {stageIndex + 1}</span>
                      <span className="stage-name">{stage.stageName}</span>
                      {isLocked && (
                        <span className="stage-lock">
                          <Lock size={16} /> Locked
                        </span>
                      )}
                    </div>
                    <span className={`stage-status-badge status-${normalizeStatus(stage.status)}`}>
                      {formatStatusLabel(stage.status)}
                    </span>
                  </div>

                  {dependencyMode && (
                    <div className="dependency-controls">
                      <button
                        className={`btn-sm ${selectedFromStage === stage.id ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() =>
                          setSelectedFromStage(selectedFromStage === stage.id ? null : stage.id || null)
                        }
                      >
                        {selectedFromStage === stage.id ? 'Source Selected' : 'Set as Source'}
                      </button>
                      {selectedFromStage && selectedFromStage !== stage.id && (
                        <button
                          className="btn-sm btn-success"
                          onClick={() => handleCreateDependency(stage.id!)}
                        >
                          Create Dependency
                        </button>
                      )}
                    </div>
                  )}

                  {expandedStages.has(stageIndex) && !dependencyMode && (
                    <div className="stage-content">
                      {/* Stage Properties */}
                      {stage.properties && stage.properties.length > 0 && (
                        <div className="stage-properties">
                          <h4>Properties</h4>
                          <div className="properties-list">
                            {stage.properties.map((prop, propIndex) => (
                              <div key={propIndex} className="property-item">
                                <label>{prop.propertyName}</label>
                                <p className="property-value">{prop.value || '-'}</p>
                                {prop.attachments && prop.attachments.length > 0 && (
                                  <div className="property-attachments">
                                    {prop.attachments.map((att) => (
                                      <a
                                        key={att.id}
                                        href={att.filePath}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="attachment-link"
                                      >
                                        <Download size={14} /> {att.fileName}
                                      </a>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="stage-actions-section">
                        <div className="actions-header">
                          <h4>Actions ({stage.actions?.length || 0})</h4>
                          {!isLocked && (
                            <button
                              className="btn-sm btn-primary"
                              onClick={() => openActionForm(stageIndex)}
                            >
                              <Plus size={14} /> Add Action
                            </button>
                          )}
                        </div>

                        <div className="actions-list">
                          {stage.actions?.map((action, actionIndex) => (
                            <div key={actionIndex} className="action-item">
                              <div className="action-header">
                                <h5>Action {action.actionNumber || actionIndex + 1}</h5>
                                <span className={`action-status status-${normalizeStatus(action.status)}`}>
                                  {formatStatusLabel(action.status)}
                                </span>
                              </div>

                              <div className="action-content">
                                <div className="action-field">
                                  <label>Action Number</label>
                                  <p>{action.actionNumber ?? actionIndex + 1}</p>
                                </div>

                                <div className="action-field">
                                  <label>Target Date</label>
                                  <p>{formatDateOnly(action.targetDate)}</p>
                                </div>

                                <div className="action-field">
                                  <label>Owner</label>
                                  <p>{action.ownerName || '-'}</p>
                                </div>

                                <div className="action-field">
                                  <label>Action Details</label>
                                  <p>{action.actionDetails || '-'}</p>
                                </div>

                                <div className="action-field">
                                  <label>Action Response</label>
                                  <p>{action.actionResponse || '-'}</p>
                                </div>

                                <div className="action-field">
                                  <label>Closed Date</label>
                                  <p>{formatDateOnly(action.closedDate)}</p>
                                </div>

                                <div className="action-field">
                                  <label>Complete Date</label>
                                  <p>{formatDateOnly(action.completeDate)}</p>
                                </div>

                                {/* Attachments */}
                                {action.attachments && action.attachments.length > 0 && (
                                  <div className="action-attachments">
                                    <label>Attachments</label>
                                    <div className="attachments-list">
                                      {action.attachments.map((att) => (
                                        <div key={att.id} className="attachment-item">
                                          <a
                                            href={att.filePath}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="attachment-name"
                                          >
                                            <Download size={14} /> {att.fileName}
                                          </a>
                                          <button
                                            type="button"
                                            className="btn-icon btn-delete-sm"
                                            onClick={() => handleDeleteFile(att.id)}
                                          >
                                            <Trash2 size={12} />
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Upload File */}
                                <div className="action-upload">
                                  <label htmlFor={`file-${stageIndex}-${actionIndex}`}>
                                    <Upload size={14} /> Upload File
                                  </label>
                                  <input
                                    id={`file-${stageIndex}-${actionIndex}`}
                                    type="file"
                                    onChange={(e) => {
                                      if (e.target.files?.[0]) {
                                        handleFileUpload(stageIndex, actionIndex, e.target.files[0]);
                                      }
                                    }}
                                    disabled={uploadingFile?.stageIndex === stageIndex && uploadingFile?.actionIndex === actionIndex}
                                  />
                                </div>
                              </div>

                              <div className="action-footer">
                                {!isLocked && (
                                  <>
                                    <button
                                      className="btn-sm btn-secondary"
                                      onClick={() => openActionForm(stageIndex, actionIndex)}
                                    >
                                      <Edit2 size={14} /> Edit
                                    </button>
                                    <button
                                      className="btn-sm btn-delete"
                                      onClick={() => handleDeleteAction(stageIndex, actionIndex)}
                                    >
                                      <Trash2 size={14} /> Delete
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Dependencies Visualization */}
        {ncr.stageDependencies && ncr.stageDependencies.length > 0 && !dependencyMode && (
          <div className="dependencies-section">
            <h2>Stage Dependencies</h2>
            <div className="dependencies-list">
              {ncr.stageDependencies.map((dep, index) => {
                const fromStage = ncr.stages.find((s) => s.id === dep.fromStageId);
                const toStage = ncr.stages.find((s) => s.id === dep.toStageId);
                return (
                  <div key={index} className="dependency-item">
                    <span className="dependency-text">
                      {fromStage?.stageName} → {toStage?.stageName} ({dep.dependencyType})
                    </span>
                    <button
                      className="btn-icon btn-delete-sm"
                      onClick={() => handleDeleteDependency(dep.id)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Action Form Modal */}
      {actionForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{actionForm.isEditing ? 'Edit Action' : 'Create Action'}</h3>
              <button
                type="button"
                className="btn-close"
                onClick={closeActionForm}
              >
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="actionNumber">Action Number</label>
                <input
                  id="actionNumber"
                  type="number"
                  value={actionForm.actionNumber ?? ''}
                  onChange={(e) =>
                    setActionForm({
                      ...actionForm,
                      actionNumber: e.target.value ? Number(e.target.value) : undefined,
                    })
                  }
                  placeholder="Enter action number"
                />
              </div>

              <div className="form-group">
                <label htmlFor="targetDate">Target Date</label>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={toDatePickerValue(actionForm.targetDate)}
                    onChange={(newValue) => setActionForm({ ...actionForm, targetDate: toDateOnlyString(newValue) })}
                    slotProps={{
                      textField: {
                        id: 'targetDate',
                        size: 'small',
                        fullWidth: true,
                      },
                    }}
                  />
                </LocalizationProvider>
              </div>

              <div className="form-group">
                <label htmlFor="actionDetails">Action Details</label>
                <textarea
                  id="actionDetails"
                  value={actionForm.actionDetails}
                  onChange={(e) =>
                    setActionForm({
                      ...actionForm,
                      actionDetails: e.target.value,
                    })
                  }
                  placeholder="Describe the action details..."
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label htmlFor="actionResponse">Action Response</label>
                <textarea
                  id="actionResponse"
                  value={actionForm.actionResponse}
                  onChange={(e) =>
                    setActionForm({
                      ...actionForm,
                      actionResponse: e.target.value,
                    })
                  }
                  placeholder="Describe the action response..."
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label htmlFor="ownerId">Owner</label>
                <input
                  id="ownerId"
                  type="number"
                  value={actionForm.ownerId || ''}
                  onChange={(e) =>
                    setActionForm({
                      ...actionForm,
                      ownerId: e.target.value ? Number(e.target.value) : undefined,
                    })
                  }
                  placeholder="Enter owner ID"
                />
              </div>

              <div className="form-group">
                <label htmlFor="closedDate">Closed Date</label>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={toDatePickerValue(actionForm.closedDate)}
                    onChange={(newValue) => setActionForm({ ...actionForm, closedDate: toDateOnlyString(newValue) })}
                    slotProps={{
                      textField: {
                        id: 'closedDate',
                        size: 'small',
                        fullWidth: true,
                      },
                    }}
                  />
                </LocalizationProvider>
              </div>

              <div className="form-group">
                <label htmlFor="completeDate">Complete Date</label>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={toDatePickerValue(actionForm.completeDate)}
                    onChange={(newValue) => setActionForm({ ...actionForm, completeDate: toDateOnlyString(newValue) })}
                    slotProps={{
                      textField: {
                        id: 'completeDate',
                        size: 'small',
                        fullWidth: true,
                      },
                    }}
                  />
                </LocalizationProvider>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeActionForm}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={saveAction}
              >
                <Save size={16} /> Save Action
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NcrDetail;
